<template>
    <aside class="flex w-96 flex-col border-l border-slate-200 bg-white">
        <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div class="min-w-0">
                <h2 class="text-sm font-semibold text-slate-800">LLM Call Logs</h2>
                <div v-if="data" class="truncate text-[11px] text-slate-400">{{ data.label }}</div>
            </div>
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="call-logs-refresh"
                    :disabled="loading"
                    @click="load">
                    {{ loading ? "Loading…" : "Refresh" }}
                </button>
                <button
                    type="button"
                    class="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                    data-testid="call-logs-close"
                    aria-label="Close call logs"
                    @click="emit('close')">
                    ✕
                </button>
            </div>
        </div>

        <div class="flex-1 overflow-y-auto px-4 py-3 text-xs">
            <div v-if="loading && !data" class="text-slate-500">Loading call logs…</div>
            <div v-else-if="error" class="text-red-600">Failed to load call logs.</div>
            <div v-else-if="data && allLogs.length === 0" class="text-slate-500">No call logs for this message.</div>

            <template v-else-if="data">
                <div v-if="data.preview" class="mb-3 line-clamp-3 text-slate-500">{{ data.preview }}</div>

                <div class="space-y-2">
                    <div v-for="log in data.call_logs" :key="log.id">
                        <CallLogRow :log="log" :raw="rawById[log.id]" @toggle-raw="toggleRaw(log.id)" />
                    </div>
                </div>

                <div v-if="data.other_call_logs.length > 0" class="mt-3">
                    <div class="mb-1 text-[10px] font-medium uppercase text-amber-600">Other calls this turn ({{ data.other_call_logs.length }})</div>
                    <div class="space-y-2">
                        <div v-for="log in data.other_call_logs" :key="log.id">
                            <CallLogRow :log="log" :raw="rawById[log.id]" @toggle-raw="toggleRaw(log.id)" />
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </aside>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import { logError } from "~vue/utils/logError";
import { computed, onMounted, reactive, ref, watch } from "vue";

import CallLogRow from "./CallLogRow.vue";
import type { CallLogEventResponse, CallLogRaw, CallLogSummary } from "./types";

const props = defineProps<{ conversationId: string; eventId: string }>();
const emit = defineEmits<{ close: [] }>();

const data = ref<CallLogEventResponse | null>(null);
const loading = ref(false);
const error = ref(false);

const allLogs = computed<CallLogSummary[]>(() => [...(data.value?.call_logs ?? []), ...(data.value?.other_call_logs ?? [])]);

// Lazy raw request/response payloads, keyed by call log id. Only fetched when a
// row's "Raw" panel is opened, so the heavy messages/response blobs stay off the
// summary request.
interface RawState {
    open: boolean;
    loading: boolean;
    data: CallLogRaw | null;
}
const rawById = reactive<Record<number, RawState>>({});

const load = async () => {
    loading.value = true;
    error.value = false;
    try {
        const { data: res } = await useFetch(`/v2/chat-call-logs/${props.conversationId}/event/${props.eventId}/`).get().json<CallLogEventResponse>();
        data.value = res.value ?? null;
        // Expand every call log's raw payload by default — clicking the
        // per-message button is itself the on-demand action, so the staff user
        // wants the details up front rather than another click per row.
        if (data.value) {
            for (const log of [...data.value.call_logs, ...data.value.other_call_logs]) {
                ensureRawLoaded(log.id);
            }
        }
    } catch (err) {
        error.value = true;
        logError(err instanceof Error ? err : new Error(String(err)));
    } finally {
        loading.value = false;
    }
};

// Ensure a log's raw payload is open and fetched (idempotent). Fired for every
// log after load, and by the row's "Raw" button when re-opening.
const ensureRawLoaded = async (logId: CallLogSummary["id"]) => {
    if (!rawById[logId]) {
        rawById[logId] = { open: true, loading: false, data: null };
    }
    const state = rawById[logId];
    state.open = true;
    if (state.data || state.loading) {
        return;
    }
    state.loading = true;
    try {
        const { data: res } = await useFetch(`/v2/chat-call-logs/raw/${logId}/`).get().json<CallLogRaw>();
        state.data = res.value ?? null;
    } catch (err) {
        logError(err instanceof Error ? err : new Error(String(err)));
    } finally {
        state.loading = false;
    }
};

const toggleRaw = (logId: CallLogSummary["id"]) => {
    const existing = rawById[logId];
    if (existing?.open) {
        existing.open = false;
        return;
    }
    ensureRawLoaded(logId);
};

watch(
    () => [props.conversationId, props.eventId],
    () => {
        data.value = null;
        load();
    },
);

onMounted(load);
</script>
